import React from 'react';

export const SEVERITY_COLORS = {
  Blocker: '#ef4444',
  Critical: '#f97316',
  Major: '#eab308',
  Minor: '#3b82f6',
  Enhancement: '#22c55e',
  Unspecified: '#6b7280',
};

const colorFor = (sev) => SEVERITY_COLORS[sev] || '#6b7280';

// Pie chart for severity overview.
export const SeverityPie = ({ slices = [], size = 200, light = false }) => {
  const total = slices.reduce((s, x) => s + x.count, 0);
  const textCls = light ? 'text-gray-700' : 'text-gray-300';
  if (total === 0) return <div className={`${light ? 'text-gray-500' : 'text-gray-500'} text-sm`}>No data</div>;

  const r = size / 2;
  const cx = r;
  const cy = r;

  const nonZero = slices.filter((s) => s.count > 0);
  const single = nonZero.length === 1;

  let angle = -Math.PI / 2;
  const arcs = nonZero.map((s) => {
    const frac = s.count / total;
    const start = angle;
    const end = angle + frac * 2 * Math.PI;
    angle = end;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const large = end - start > Math.PI ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
    return { d, color: colorFor(s.severity), key: s.severity };
  });

  return (
    <div className="flex items-center gap-6 flex-wrap">
      <svg width={size} height={size}>
        {single ? (
          <circle cx={cx} cy={cy} r={r} fill={colorFor(nonZero[0].severity)} stroke={light ? '#ffffff' : '#0a0a0a'} strokeWidth="1" />
        ) : (
          arcs.map((a) => (
            <path key={a.key} d={a.d} fill={a.color} stroke={light ? '#ffffff' : '#0a0a0a'} strokeWidth="1" />
          ))
        )}
      </svg>
      <ul className="space-y-1 text-sm">
        {slices.map((s) => (
          <li key={s.severity} className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: colorFor(s.severity) }} />
            <span className={`w-24 ${light ? 'text-gray-800' : 'text-white'}`}>{s.severity}</span>
            <span className={textCls}>{s.count} ({s.percent}%)</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Vertical stacked bars per domain, segmented by severity.
export const DomainBars = ({ bars = [], height = 220, light = false }) => {
  if (bars.length === 0) return <div className="text-gray-500 text-sm">No data</div>;
  const max = Math.max(...bars.map((b) => b.total), 1);
  const labelCls = light ? 'text-gray-700' : 'text-gray-300';
  const subCls = light ? 'text-gray-600' : 'text-gray-400';

  return (
    <div>
      <div className="flex items-end gap-4 overflow-x-auto pb-2" style={{ height }}>
        {bars.map((b) => (
          <div key={b.domain} className="flex flex-col items-center justify-end h-full min-w-[44px]">
            <span className={`text-xs ${labelCls} mb-1`}>{b.total}</span>
            <div
              className="w-9 flex flex-col-reverse rounded-t overflow-hidden"
              style={{ height: `${(b.total / max) * (height - 40)}px` }}
            >
              {b.segments.map((seg) => (
                <div
                  key={seg.severity}
                  title={`${seg.severity}: ${seg.count}`}
                  style={{ background: colorFor(seg.severity), flexGrow: seg.count }}
                />
              ))}
            </div>
            <span className={`text-xs ${subCls} mt-1 max-w-[60px] truncate`} title={b.domain}>{b.domain}</span>
          </div>
        ))}
      </div>
      <div className={`flex flex-wrap gap-3 pt-3 text-xs ${subCls} border-t ${light ? 'border-gray-200' : 'border-gray-800'} mt-2`}>
        {Object.keys(SEVERITY_COLORS).map((sev) => (
          <span key={sev} className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: SEVERITY_COLORS[sev] }} />
            {sev}
          </span>
        ))}
      </div>
    </div>
  );
};
